import React, { useEffect, useState, forwardRef, useImperativeHandle, useRef, useCallback } from 'react'
import { useDebounceFn, useRequest } from 'ahooks'
import request from 'meinian-package/src/utils/request'
import Scroll from '@yk-component/yk-scroll'
import Loading from 'meinian-package/src/baseComponent/loading'
import { Empty } from 'meinian-package/src/baseComponent'
import { uuid } from 'meinian-package/src/utils'
import { flushSync } from 'react-dom'
import HasEmpty from 'component/hasEmpty'
import Masonry from '../baseMasonry'

export const RULES = {
  L: {
    widths: [1910, 1638, 1110, 500],
    maxColumnCount: 5
  },
  M: {
    widths: [1910, 1638, 1430, 1110, 900, 700],
    maxColumnCount: 7
  },
  S: {
    widths: [1910, 1638, 1430, 1110, 900, 700, 500],
    maxColumnCount: 8
  }
}

interface IMasonryApi{
  [other:string]: any,
}

export default forwardRef(({
  params,
  url,
  method = 'post',
  afterLoad,
  cardProps,
  loadingText,
  root,
  columnCount,
  rule = RULES.M,
  gutter = 16,
  cardKey,
  renderItem,
  scrollIntoViewOptions,
  endText,
  scrollToTop = true,
  empty,
  disabledIsEnd = false,
  emptyType,
  emptyText,
  pageSize = 20,
  onTotal
}: IMasonryApi, ref) => {
  const [data, setData] = useState<any>([])
  const [total, setTotal] = useState(0)
  const [isEnd, setIsEnd] = useState(false)
  const [href, setHref] = useState('')
  const [loading, setLoading] = useState(true)
  const $wrap = useRef()

  useEffect(() => {
    setHref(location.href)
  }, [])

  const getAutoColumnCount = useCallback((width) => {
    for (let i = 0; i < rule.widths.length; i++) {
      if (width >= rule.widths[i]) return rule.maxColumnCount - i
    }
  }, [rule])

  const { error, run, params: [start] } = useRequest((s) => request[method](`${url}?start=${s}&pageSize=${pageSize}`, {
    ...params,
    start: s,
    pageSize,
    sortValues: !s ? undefined : data?.slice(-1)[0]?.sortValues,
  }), {
    manual: true,
    ready: !!url,
    onBefore: () => {
      setLoading(true)
    },
    onSuccess: (result:any) => {
      setLoading(false)
      setTotal(result.resultCount)
      onTotal?.(result.resultCount)
      if (!result?.resultList?.length || start + pageSize >= result.resultCount) {
        setIsEnd(true)
      }
      if (!result?.resultList?.length) {
        return
      }
      const resultList = result.resultList.map(m => ({ ...m, uuidKey: uuid() }))
      if (start === 0) {
        setData(resultList || [])
      } else {
        data.push(...resultList)
        setData([...data])
      }
      afterLoad && afterLoad(start === 0 ? resultList : data, result)
    },
    onFinally() {
      setLoading(false)
    },
    onError(err) {
      setLoading(false)
      console.error(url, err)
      afterLoad && afterLoad(null, {})
    }
  })

  const { run: onEnd } = useDebounceFn(() => {
    if (url && !loading) {
      run(start + pageSize)
    }
  }, {
    wait: 300
  })

  const $top = useRef<any>()
  const $masonry = useRef<any>()

  useImperativeHandle(ref, () => ({
    init: () => { init() },
    total,
    data: () => data,
    layout: () => onResize(),
  }))

  const onResize = () => {
    setData(pre => [...pre])
    $masonry.current?.layout()
  }

  useEffect(() => {
    if (url) {
      scrollToTop && $top.current?.scrollIntoView(scrollIntoViewOptions || false)
      init()
    }
  }, [url, JSON.stringify(params)])

  const init = () => {
    setTimeout(() => {
      flushSync(() => { // 当瀑布流的滚动条滑倒底部的时候 的 init 会跟 onEnd 同时执行导致问题 现在这里不需要批处理 单独处理init
        setIsEnd(false)
        setData([])
        run(0)
      })
    }, 0)
  }

  const onDeleteItem = (item) => {
    setData(data.filter(m => m !== item))
  }

  const onItemDataChange = () => {
    setData(pre => [...pre])
  }

  const scrollTo = (outerIndex) => {
    const cardDom = document.querySelector(`[yk-masonry-index='${outerIndex}']`)
    cardDom && cardDom.scrollIntoView(false)
  }
  const onChangeIndex = (outerIndex) => {
    if (outerIndex >= data?.length) {
      onEnd()
    }
    scrollTo(outerIndex)
  }
  function render() {
    return <Loading spinning={loading} wrapperClassName="spin-transparent-bg">
      <HasEmpty
        loading={loading}
        length={data?.length || 0}
        emptySlot={empty || <Empty type={emptyType} title={emptyText} />}
      >
        <Scroll
          onEnd={onEnd}
          isEnd={disabledIsEnd || isEnd}
          loading={loading}
          root={() => root || document.body}
          marginBottom={200}
        >
          <Masonry
            instance={$masonry}
            data={data}
            itemKey={(item, index) => (cardKey ? cardKey(item, index) : item.uuidKey)}
            gutter={gutter}
            zIndex={i => -i}
            renderItem={(item, width, index) => renderItem?.({
              ...item,
              ...cardProps,
              item,
              itemData: item,
              href,
              columnWidth: width,
              columnCount: (_width) => columnCount || getAutoColumnCount(_width),
              outerIndex: index,
              data,
              onChangeIndex,
              onDeleteItem: () => onDeleteItem(item),
              onItemDataChange,
              total,
              onResize,
              initWaterfall: init,
              setData: d => setData(d),
            })}
            columnCount={(_width) => columnCount || getAutoColumnCount(_width)}
          />
        </Scroll>
        {!isEnd && loading && !error ? <div style={{ textAlign: 'center', color: '#999999', padding: 32 }}>{loadingText || '加载中~'}</div> : null}
        {error ? <div style={{ textAlign: 'center', color: '#999999', padding: 32 }}>{loadingText || '网络错误,请刷新'}</div> : null}
        {isEnd && !loading && <div style={{ textAlign: 'center', color: '#999999', padding: 32 }}>{endText || '没有更多啦~'}</div>}
      </HasEmpty>
    </Loading>
  }
  return <div ref={$wrap} style={{ margin: 'auto' }}>
    <div ref={$top} />
    {render()}
  </div>
})
